/* eslint-disable react/prop-types */
import {useEffect, useMemo, useRef, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {becomesCommunication, communicationWarning} from './communicationWarning';
import {FELICITY_FAB_CLEARANCE} from '../../../constants/felicity';
import {
    Alert,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    styled,
} from '@mui/material';
import Typography from '@mui/material/Typography';
import FormStructure from './FormStructure';
import PrivateFormSettings from './PrivateFormSettings/PrivateFormSettings';
import PropTypes from 'prop-types';
import {FORM_ACCESS_LEVELS} from '../../../constants/formAccessLevels';
import PublicFormSettings from './PublicFormSettings';
import {
    useCreateFormMutation,
    useLazyGetFormQuery,
    useUpdateFormMutation,
    useUpdateFormStatusMutation
} from '../../../api/services/formsService';
import useNotification from '../../../utility/hooks/useNotification';
import {FORM_STATUSES} from '../../../constants/formStatuses';
import {useGetFormCategoriesQuery} from '../../../api/services/formCategoriesService';
import {useGetCurrentSchoolQuery} from '../../../api/services/schoolManagementService';
import useUser from '../../../utility/hooks/useUser';
import FORM_AUDIENCE_TYPE from '../../../constants/FormAudienceType';
import {sanitizeTriggerRecipientFieldIds} from './PrivateFormSettings/TriggerFormSettings';
import FormTemplateType from '../../../constants/FormTemplateType';
import {
    useCreateExternalUserFormAssignmentsMutation,
    useCreateFormEmployeeAssignmentMutation,
    useCreateFormQuestionAssignmentMutation,
    useCreateFormStudentAssignmentMutation,
    useDeleteExternalUserFormAssignmentsMutation,
    useDeleteFormAssignmentsMutation,
    useDeleteFormQuestionAssignmentMutation,
    useDeleteFormStudentsAssignmentsMutation,
    useGetFormEmployeeAssignmentsQuery,
    useGetFormStudentsAssignmentsQuery,
    useGetExternalUserFormAssignmentsQuery,
    useGetFormQuestionAssignmentQuery
} from '../../../api/services/formAssignmentsService';
import {useConfirm} from 'material-ui-confirm';
import Tooltip from '@mui/material/Tooltip';
import FORM_ASSIGNMENT_TYPE from '../../../constants/FormAssignmentType';
import AdmicityTabs from '../../../shared-components/AdmicityTabs';
import useMediaQuery from '@mui/material/useMediaQuery';
import AdmicityDialog from '../../../shared-components/AdmicityDialog';
import {
    useLazyGetStudentFiltersQuery,
    useGetStudentFiltersQuery,
} from '../../../api/services/studentService';
import {parseRemindIntervalInput, validateAccessLevel, validateGroupFilters} from '../../../utility/validationUtil';
import {DateTimePicker} from '@mui/x-date-pickers';
import moment from 'moment/moment';
import {formatFormExpirationDate} from '../../../utility/dateUtil';
import CancelIcon from '@mui/icons-material/Close';
import {
    PARENT_AUDIENCE_TYPE,
    resolveBackendAudiencePayload,
    resolveFrontendAudience,
} from '../../../constants/ParentAudienceType';
import FORM_TYPE from '../../../constants/FormType';

const audienceOptionsByFormTemplateType =
    {
        transition: [{
            value: FORM_AUDIENCE_TYPE.previousSchool,
            label: 'Child\'s previous school'
        }],
        offer: [{
            value: FORM_AUDIENCE_TYPE.parentsForChildCompletion,
            label: 'Parent of applicant with Applied or Offered status'
        }]
    };

const getDefaultAudienceOptions = (includeEnquiries = false) => ([
    {
        value: FORM_AUDIENCE_TYPE.parentsForChildCompletion,
        label: 'Parent'
    },
    {
        value: FORM_AUDIENCE_TYPE.employees,
        label: 'Employee'
    },
    {
        value: FORM_AUDIENCE_TYPE.externalUsers,
        label: 'External recipient'
    },
    {
        value: FORM_AUDIENCE_TYPE.students,
        label: 'Student'
    },
    ...(includeEnquiries
        ? [{
            value: FORM_AUDIENCE_TYPE.enquiries,
            label: 'Enquiry'
        }]
        : [])
]);
const FOOTER_ACTION_BUTTON_WIDTH = 180;
const FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE = {xs: 150, lg: FOOTER_ACTION_BUTTON_WIDTH};

const normalizeLegacyParentAudienceType = ({
    audience,
    parentAudienceType,
    accessLevel,
    templateType,
}) => {
    const numericAudience = Number(audience);
    const numericParentAudienceType = Number(parentAudienceType ?? PARENT_AUDIENCE_TYPE.none);

    if (numericParentAudienceType !== PARENT_AUDIENCE_TYPE.none) {
        return numericParentAudienceType;
    }

    if (numericAudience !== FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
        return numericParentAudienceType;
    }

    if (accessLevel === FORM_ACCESS_LEVELS.private) {
        return PARENT_AUDIENCE_TYPE.withAccount;
    }

    if (templateType === FormTemplateType.offer) {
        return PARENT_AUDIENCE_TYPE.applicant;
    }

    if (accessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink) {
        return PARENT_AUDIENCE_TYPE.withoutAccount;
    }

    return numericParentAudienceType;
};
const stripRecipientMeta = (items = []) =>
    items
        .filter(item => item && !item.isDeleted && !item.deleted)
        .map(({
                  isDeleted,
                  isNew,
                  deleted,
                  ...rest
              }) => rest);

const getRecipientSortLabel = (item = {}) =>
    item.label ||
    item.displayName ||
    item.name ||
    item.email ||
    `${item.preferredForename ?? ''} ${item.preferredSurname ?? ''}`.trim();

const sortRecipientFilters = (items = []) =>
    [...items].sort((a, b) =>
        getRecipientSortLabel(a).localeCompare(getRecipientSortLabel(b), undefined, {sensitivity: 'base'})
    );

const hasActiveRecipientFilters = (items = []) =>
    items.some((item) => item && !item.isDeleted && !item.deleted);

const isEnquiryGroupFilter = (filter = {}) =>
    String(filter.displayName ?? '').toUpperCase().startsWith('ENQUIRY');

const hasLegacyEnquiryEvidence = (settings = {}) =>
    hasActiveRecipientFilters(settings.enquiryFilters) ||
    hasActiveRecipientFilters(settings.linkedEnquiryFilters) ||
    (settings.groupFilters || []).some((filter) =>
        filter &&
        !filter.isDeleted &&
        !filter.deleted &&
        isEnquiryGroupFilter(filter)
    ) ||
    (settings.linkedGroupFilters || []).some((filter) =>
        filter &&
        !filter.isDeleted &&
        !filter.deleted &&
        isEnquiryGroupFilter(filter)
    );

const getRecipientSettingsSnapshot = (settings = {}) => ({
    groupFilters: stripRecipientMeta(settings.groupFilters),
    studentFilters: sortRecipientFilters(stripRecipientMeta(settings.studentFilters)),
    applicationFilters: sortRecipientFilters(stripRecipientMeta(settings.applicationFilters)),
    enquiryFilters: sortRecipientFilters(stripRecipientMeta(settings.enquiryFilters)),
    employeeFilters: sortRecipientFilters(stripRecipientMeta(settings.employeeFilters)),
    externalUserEmailAssignments: sortRecipientFilters(stripRecipientMeta(settings.externalUserEmailAssignments)),
    linkedGroupFilters: stripRecipientMeta(settings.linkedGroupFilters),
    linkedStudentFilters: sortRecipientFilters(stripRecipientMeta(settings.linkedStudentFilters)),
    linkedApplicationFilters: sortRecipientFilters(stripRecipientMeta(settings.linkedApplicationFilters)),
    linkedEnquiryFilters: sortRecipientFilters(stripRecipientMeta(settings.linkedEnquiryFilters)),
    linkedEmployeeFilters: sortRecipientFilters(stripRecipientMeta(settings.linkedEmployeeFilters)),
    linkedStrictFiltering: !!settings.linkedStrictFiltering,
    assignmentType: settings.assignmentType ?? FORM_ASSIGNMENT_TYPE.students
});

const buildPupilFilters = (pupils, assignmentData) => {
    const assignmentsMap = (assignmentData?.personAssignments ?? []).reduce((acc, a) => {
        acc[a.personId] = a;
        return acc;
    }, {});

    const filters = (pupils ?? []).reduce((acc, pupil) => {
        const a = assignmentsMap[pupil.id];
        if (!a) return acc;

        const item = {
            id: pupil.id,
            label: a.displayName,
            assignmentId: a.id
        };

        if (pupil.isApplicant) {
            acc.applicationFilters.push(item);
        } else if (pupil.isEnquiry) {
            acc.enquiryFilters.push(item);
        } else {
            acc.studentFilters.push(item);
        }

        return acc;
    }, {
        studentFilters: [],
        applicationFilters: [],
        enquiryFilters: []
    });

    return {
        studentFilters: sortRecipientFilters(filters.studentFilters),
        applicationFilters: sortRecipientFilters(filters.applicationFilters),
        enquiryFilters: sortRecipientFilters(filters.enquiryFilters)
    };
};

const FormEditor = ({
                        wizardAccessLevel,
                        mode,
                        wizard,
                        setWizard,
                        formId: initialFormId,
                        isWizardMode,
                        schema: externalSchema,
                        onSchemaChange: externalOnSchemaChange,
                        onSchemaValidationChange: externalOnSchemaValidationChange,
                          audience: wizardAudience,
                          parentAudienceType: wizardParentAudienceType,
                          recipientType: wizardRecipientType,
                          linkedToStudents: wizardLinkedToStudents,
                          linkedToEmployees: wizardLinkedToEmployees,
                          triggerSourceAudience: wizardTriggerSourceAudience,
                          wizardAllLinkedEmployeesCanSubmit,
                          wizardLinkedStrictFiltering,
                          wizardIsCommunicationForm,
                          wizardFormType,
                          scrollContainerRef,
                          onScrollContainerScroll,
                      }) => {
    const isEditMode = mode === 'edit';
    const params = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [formId, setFormId] = useState(() =>
        isWizardMode
            ? (initialFormId != null ? Number(initialFormId) : null)
            : (params.id ? Number(params.id) : null)
    );

    const confirm = useConfirm();
    const {
        showErrorNotification,
        showSuccessNotification,
        showDetailedErrorNotification,
        showInfoNotification
    } = useNotification();

    const [localSchema, setLocalSchema] = useState(undefined);

    const schema = isWizardMode ? externalSchema : localSchema;
    const setSchema = isWizardMode
        ? (externalOnSchemaChange ?? (() => {
        }))
        : setLocalSchema;

    const [schemaValidation, setSchemaValidation] = useState({});
    const handleSchemaValidationChange = (nextValidation) => {
        setSchemaValidation(nextValidation);

        if (typeof externalOnSchemaValidationChange === 'function') {
            externalOnSchemaValidationChange(nextValidation);
        }
    };

    const [componentToEditIndex, setComponentToEditIndex] = useState(undefined);
    const [formStatus, setFormStatus] = useState(undefined);
    const [template, setTemplate] = useState(undefined);
    const [localAccessLevel, setLocalAccessLevel] = useState(null);

    const accessLevel = isWizardMode
        ? (wizardAccessLevel ?? wizard?.details?.accessLevel ?? null)
        : localAccessLevel;

    const setAccessLevel = (next) => {
        if (isWizardMode) return;
        setLocalAccessLevel(next);
    };

    const [expirationDate, setExpirationDate] = useState();
    const [remindInterval, setRemindInterval] = useState(0);
    const [strictRecipientsFiltering, setStrictRecipientsFiltering] = useState(false);
    const [allParentsCanSubmit, setAllParentsCanSubmit] = useState(false);
    const [sendToPriority1ContactsOnly, setSendToPriority1ContactsOnly] = useState(false);
    const [visibleToParents, setVisibleToParents] = useState(true);
    const [shareResponseWithOtherParents, setShareResponseWithOtherParents] = useState(true);
    const [showResponsesInLinkedProfile, setShowResponsesInLinkedProfile] = useState(false);
    const [showResponsesToLinkedParents, setShowResponsesToLinkedParents] = useState(false);
    const [profileMustBeComplete, setProfileMustBeComplete] = useState(false);
    const [allowMultipleSubmissions, setAllowMultipleSubmissions] = useState(false);
    const [enableRecipientNotifications, setEnableRecipientNotifications] = useState(true);
    const [name, setName] = useState('');
    const [categories, setCategories] = useState([]);
    const [hasInitialisedLinkedStudents, setHasInitialisedLinkedStudents] = useState(false);
    const [hasInitialisedLinkedEmployees, setHasInitialisedLinkedEmployees] = useState(false);
    const [selectedAudience, setSelectedAudience] = useState(FORM_AUDIENCE_TYPE.none);
    const [localParentAudienceType, setLocalParentAudienceType] = useState(PARENT_AUDIENCE_TYPE.none);
    const [shouldInferLegacyParentAudienceType, setShouldInferLegacyParentAudienceType] = useState(false);

    const audience = isWizardMode
        ? (wizardAudience ?? FORM_AUDIENCE_TYPE.none)
        : selectedAudience;
    const parentAudienceType = isWizardMode
        ? (wizardParentAudienceType ?? PARENT_AUDIENCE_TYPE.none)
        : localParentAudienceType;

    const [notificationEmails, setNotificationEmails] = useState([]);
    const [enableAutoresponder, setEnableAutoresponder] = useState(false);
    const [isAtBottom, setIsAtBottom] = useState(false);
    const [isCommunicationForm, setIsCommunicationForm] = useState(false);
    const [linkToStudents, setLinkToStudents] = useState(false);
    const [linkToEmployees, setLinkToEmployees] = useState(false);
    const [allLinkedEmployeesCanSubmit, setAllLinkedEmployeesCanSubmit] = useState(false);
    const [isGoLiveDialogOpen, setIsGoLiveDialogOpen] = useState(false);
    const [isScheduleGoLiveDialogOpen, setIsScheduleGoLiveDialogOpen] = useState(false);
    const [scheduledGoLiveDate, setScheduledGoLiveDate] = useState(null);
    const [scheduledGoLiveDateError, setScheduledGoLiveDateError] = useState('');

    useEffect(() => {
        if (!isWizardMode) {
            return;
        }

        setIsCommunicationForm(!!wizardIsCommunicationForm);
        setLinkToStudents(!!wizardLinkedToStudents);
        setLinkToEmployees(!!wizardLinkedToEmployees);
        setAllLinkedEmployeesCanSubmit(!!wizardAllLinkedEmployeesCanSubmit);
        setPrivateFormSettings(prev => ({
            ...prev,
            linkedStrictFiltering: !!wizardLinkedStrictFiltering,
            allLinkedEmployeesCanSubmit: !!wizardAllLinkedEmployeesCanSubmit,
        }));
    }, [
        isWizardMode,
        wizardIsCommunicationForm,
        wizardLinkedToStudents,
        wizardLinkedToEmployees,
        wizardAllLinkedEmployeesCanSubmit,
        wizardLinkedStrictFiltering,
    ]);

    const [privateFormSettings, setPrivateFormSettings] = useState({
        name: name ?? '',
        expirationDate: expirationDate ?? null,
        remindInterval: remindInterval ?? 0,
        categories: categories ?? [],
        groupFilters: [],
        studentFilters: [],
        applicationFilters: [],
        enquiryFilters: [],
        employeeFilters: [],
        externalUserEmailAssignments: [],
        linkedGroupFilters: [],
        linkedStudentFilters: [],
        linkedApplicationFilters: [],
        linkedEnquiryFilters: [],
        linkedEmployeeFilters: [],
        linkedStrictFiltering: false,
        allLinkedEmployeesCanSubmit: false,
        triggerFormSettings: {
            selectedForm: null,
            selectedFormSchema: null,
            relatedComponent: null,
            targetState: null,
            relatedFormEmailFieldId: null,
            relatedFormEmployeeFieldId: null
        },
        validation: {
            isValid: true,
            error: ''
        },
        assignmentType: FORM_ASSIGNMENT_TYPE.students
    });
    const [originalRecipientSettings, setOriginalRecipientSettings] = useState(() =>
        getRecipientSettingsSnapshot({
            assignmentType: FORM_ASSIGNMENT_TYPE.students,
            linkedGroupFilters: [],
            linkedStudentFilters: [],
            linkedApplicationFilters: [],
            linkedEnquiryFilters: [],
            linkedStrictFiltering: false
        })
    );
    const [audienceOptions, setAudienceOptions] = useState([]);
    const [hasInitialisedGroupFilters, setHasInitialisedGroupFilters] = useState(false);
    const [hasInitialisedStudentRecipients, setHasInitialisedStudentRecipients] = useState(false);
    const [hasInitialisedEmployeeRecipients, setHasInitialisedEmployeeRecipients] = useState(false);
    const [hasInitialisedExternalRecipients, setHasInitialisedExternalRecipients] = useState(false);
    const [enableTrigger, setEnableTrigger] = useState(false);
    const isSetupFlow = !isWizardMode && isEditMode && location.state?.setupFlow === true;

    const fetchEmployeeAssignments =
        audience === FORM_AUDIENCE_TYPE.employees ||
        audience === FORM_AUDIENCE_TYPE.externalUsers;

    const fetchStudentsAssignments =
        audience === FORM_AUDIENCE_TYPE.students ||
        audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
        audience === FORM_AUDIENCE_TYPE.enquiries ||
        audience === FORM_AUDIENCE_TYPE.previousSchool ||
        audience === FORM_AUDIENCE_TYPE.externalUsers;

    const recipientType = useMemo(() => {
        if (audience !== FORM_AUDIENCE_TYPE.externalUsers) return null;

        if (isWizardMode) return wizardRecipientType ?? null;

        const triggerAssignmentType =
            privateFormSettings.triggerFormSettings?.selectedForm?.assignmentType ?? null;

        const effectiveAssignmentType =
            enableTrigger
                ? triggerAssignmentType
                : (privateFormSettings.assignmentType ?? null);

        if (effectiveAssignmentType === FORM_ASSIGNMENT_TYPE.employees) return 'employee';
        if (effectiveAssignmentType === FORM_ASSIGNMENT_TYPE.students) return 'student';

        return null;
    }, [
        audience,
        isWizardMode,
        wizardRecipientType,
        enableTrigger,
        privateFormSettings.triggerFormSettings?.selectedForm?.assignmentType,
        privateFormSettings.assignmentType,
    ]);

    const externalEffectiveAssignmentType = useMemo(() => {
        if (audience !== FORM_AUDIENCE_TYPE.externalUsers) return null;

        if (isWizardMode) {
            if (wizardRecipientType === 'employee') return FORM_ASSIGNMENT_TYPE.employees;
            if (wizardRecipientType === 'student') return FORM_ASSIGNMENT_TYPE.students;
            return privateFormSettings.assignmentType ?? FORM_ASSIGNMENT_TYPE.students;
        }

        const triggerAssignmentType =
            privateFormSettings.triggerFormSettings?.selectedForm?.assignmentType ?? null;

        const effectiveAssignmentType =
            enableTrigger
                ? triggerAssignmentType
                : (privateFormSettings.assignmentType ?? null);

        return effectiveAssignmentType ?? FORM_ASSIGNMENT_TYPE.students;
    }, [
        audience,
        isWizardMode,
        wizardRecipientType,
        enableTrigger,
        privateFormSettings.triggerFormSettings?.selectedForm?.assignmentType,
        privateFormSettings.assignmentType,
    ]);

    const assignmentTypeForRecipients = useMemo(() => {
        if (audience === FORM_AUDIENCE_TYPE.employees) return FORM_ASSIGNMENT_TYPE.employees;
        if (audience === FORM_AUDIENCE_TYPE.externalUsers) return externalEffectiveAssignmentType ?? FORM_ASSIGNMENT_TYPE.students;
        return FORM_ASSIGNMENT_TYPE.students;
    }, [audience, externalEffectiveAssignmentType]);
    const baseLinkedToStudents = isWizardMode
        ? !!wizardLinkedToStudents
        : linkToStudents;
    const baseLinkedToEmployees = isWizardMode
        ? !!wizardLinkedToEmployees
        : linkToEmployees;
    const effectiveLinkedToStudents = !isCommunicationForm && baseLinkedToStudents;
    const effectiveLinkedToEmployees = !isCommunicationForm && baseLinkedToEmployees;
    const triggerSourceAudience = isWizardMode
        ? (wizardTriggerSourceAudience ?? null)
        : (enableTrigger
            ? privateFormSettings.triggerFormSettings?.selectedForm?.audience ?? null
            : null);

    const shouldFetchLinkedStudents =
        !!formId &&
        audience === FORM_AUDIENCE_TYPE.employees &&
        privateFormSettings.assignmentType === FORM_ASSIGNMENT_TYPE.employees &&
        effectiveLinkedToStudents === true;
    const shouldFetchLinkedEmployees =
        !!formId &&
        audience === FORM_AUDIENCE_TYPE.employees &&
        privateFormSettings.assignmentType === FORM_ASSIGNMENT_TYPE.employees &&
        effectiveLinkedToEmployees === true;

    const isSmallScreen = useMediaQuery('(max-width:1100px)');
    const {data: formCategories} = useGetFormCategoriesQuery();
    const {data: currentSchool} = useGetCurrentSchoolQuery();

    const [saveForm, {isLoading: isFormSaving}] = isEditMode || formId != null
        ? useUpdateFormMutation()
        : useCreateFormMutation();

    const [updateFormStatus, {isLoading: isPublishingForm}] = useUpdateFormStatusMutation();
    const [getForm] = useLazyGetFormQuery();
    const [getTriggerForm] = useLazyGetFormQuery();

    const [createFormStudentAssignments] = useCreateFormStudentAssignmentMutation();
    const [createFormEmployeeAssignment] = useCreateFormEmployeeAssignmentMutation();
    const [createExternalUserFormAssignments] = useCreateExternalUserFormAssignmentsMutation();
    const [deleteFormAssignments] = useDeleteFormAssignmentsMutation();
    const [deleteFormStudentsAssignments] = useDeleteFormStudentsAssignmentsMutation();
    const [deleteExternalUserFormAssignments] = useDeleteExternalUserFormAssignmentsMutation();
    const [createFormQuestionAssignment] = useCreateFormQuestionAssignmentMutation();
    const [deleteFormQuestionAssignments] = useDeleteFormQuestionAssignmentMutation();
    const [getStudentFilters] = useLazyGetStudentFiltersQuery();
    const {
        data: studentFormAssignmentData,
        isSuccess: isFetchStudentFormAssignmentSuccess,
        refetch: refetchStudentAssignments,
    } = useGetFormStudentsAssignmentsQuery(
        {
            formId,
            linkedStudent: false
        },
        {skip: !formId || !fetchStudentsAssignments}
    );

    const {
        data: employeeFormAssignmentData,
        isSuccess: isFetchEmployeeFormAssignmentSuccess,
        refetch: refetchEmployeeAssignments,
    } = useGetFormEmployeeAssignmentsQuery(
        {
            formId,
            linkedStudent: false
        },
        {skip: !formId || !fetchEmployeeAssignments}
    );

    const {
        data: linkedStudentAssignmentData,
        isSuccess: isFetchLinkedStudentAssignmentsSuccess,
        refetch: refetchLinkedStudentAssignments,
    } = useGetFormStudentsAssignmentsQuery(
        {
            formId,
            linkedStudent: true
        },
        {skip: !shouldFetchLinkedStudents}
    );

    const {
        data: linkedEmployeeAssignmentData,
        isSuccess: isFetchLinkedEmployeeAssignmentsSuccess,
        refetch: refetchLinkedEmployeeAssignments,
    } = useGetFormEmployeeAssignmentsQuery(
        {
            formId,
            linkedStudent: true
        },
        {skip: !shouldFetchLinkedEmployees}
    );

    const effectiveAssignments = useMemo(() => {
        if (assignmentTypeForRecipients === FORM_ASSIGNMENT_TYPE.employees) {
            return employeeFormAssignmentData ?? {
                personAssignments: [],
                groupFilters: []
            };
        }

        return studentFormAssignmentData ?? {
            personAssignments: [],
            groupFilters: []
        };
    }, [assignmentTypeForRecipients, employeeFormAssignmentData, studentFormAssignmentData]);

    const {
        data: externalUserFormAssignments,
        refetch: refetchExternalUserFormAssignments
    } = useGetExternalUserFormAssignmentsQuery(
        {formId},
        {
            skip: !formId || audience !== FORM_AUDIENCE_TYPE.externalUsers
        }
    );
    const studentIds =
        studentFormAssignmentData?.personAssignments?.map(x => x.personId) ?? [];

    const {data: pupilsData} = useGetStudentFiltersQuery(
        {body: {studentIds}},
        {skip: !audience || studentIds.length === 0}
    );

    const linkedStudentIds =
        linkedStudentAssignmentData?.personAssignments?.map(x => x.personId) ?? [];

    const {data: linkedPupilsData} = useGetStudentFiltersQuery(
        {body: {studentIds: linkedStudentIds}},
        {skip: !shouldFetchLinkedStudents || linkedStudentIds.length === 0}
    );
    const {
        data: formQuestionAssigment,
        refetch: refetchFormQuestionAssignment
    } = useGetFormQuestionAssignmentQuery(formId, {
        skip: !formId || isWizardMode
    });

    const formRef = useRef(null);
    const formAccessRef = useRef(null);
    const {user} = useUser();

    const schoolLogoSchemaComponent = useMemo(() => ({
        type: 'schoolLogo',
        url: user.schoolLogoUrl
    }), [user.schoolLogoUrl]);

    const navigateToFormsAfterPublish = (isCommunication) => {
        const activeTabIndex = isCommunication ? 5 : 1;
        navigate('/forms', {state: {activeTabIndex}});
    };

    const mapGroupFilters = (groupFilters = []) =>
        groupFilters.map((x) => ({
            id: x.filterId,
            displayName: x.displayName,
            groupId: x.id,
            excluded: x.excluded,
            deleted: x.deleted,
        }));

    const mapStudentRecipientFilters = (personAssignments = [], profiles = []) => {
        const assignmentsMap = personAssignments.reduce((acc, item) => {
            acc[item.personId] = item;
            return acc;
        }, {});

        const filters = (profiles ?? []).reduce((acc, pupil) => {
            const assignment = assignmentsMap[pupil.id];

            if (!assignment) return acc;

            const filter = {
                id: pupil.id,
                label: assignment.displayName,
                assignmentId: assignment.id
            };

            if (pupil.isApplicant) {
                acc.applicationFilters.push(filter);
            } else if (pupil.isStudent) {
                acc.studentFilters.push(filter);
            } else if (pupil.isEnquiry) {
                acc.enquiryFilters.push(filter);
            }

            return acc;
        }, {
            studentFilters: [],
            applicationFilters: [],
            enquiryFilters: []
        });

        return {
            studentFilters: sortRecipientFilters(filters.studentFilters),
            applicationFilters: sortRecipientFilters(filters.applicationFilters),
            enquiryFilters: sortRecipientFilters(filters.enquiryFilters)
        };
    };

    const buildStudentRecipientFilters = async (personAssignments = []) => {
        if (!personAssignments.length) {
            return {
                studentFilters: [],
                applicationFilters: [],
                enquiryFilters: []
            };
        }

        const studentIds = personAssignments.map(x => x.personId);
        const profiles = await getStudentFilters({
            body: {studentIds}
        })
            .unwrap()
            .catch(() => []);

        return mapStudentRecipientFilters(personAssignments, profiles);
    };

    const refreshRecipientSettingsFromServer = async () => {
        const nextSettings = {};

        if (privateFormSettings.assignmentType === FORM_ASSIGNMENT_TYPE.students) {
            const refreshedStudentAssignments = await refetchStudentAssignments();
            const assignmentData = refreshedStudentAssignments.data ?? {
                personAssignments: [],
                groupFilters: []
            };
            const studentRecipientFilters = await buildStudentRecipientFilters(assignmentData.personAssignments ?? []);

            Object.assign(nextSettings, {
                groupFilters: mapGroupFilters(assignmentData.groupFilters ?? []),
                ...studentRecipientFilters,
                employeeFilters: [],
                assignmentType: FORM_ASSIGNMENT_TYPE.students
            });
        }

        if (privateFormSettings.assignmentType === FORM_ASSIGNMENT_TYPE.employees) {
            const refreshedEmployeeAssignments = await refetchEmployeeAssignments();
            const assignmentData = refreshedEmployeeAssignments.data ?? {
                personAssignments: [],
                groupFilters: []
            };

            Object.assign(nextSettings, {
                groupFilters: mapGroupFilters(assignmentData.groupFilters ?? []),
                employeeFilters: sortRecipientFilters((assignmentData.personAssignments ?? []).map((item) => ({
                    id: item.personId,
                    assignmentId: item.id,
                    label: item.displayName
                }))),
                studentFilters: [],
                applicationFilters: [],
                enquiryFilters: [],
                assignmentType: FORM_ASSIGNMENT_TYPE.employees
            });
        }

        if (audience === FORM_AUDIENCE_TYPE.externalUsers) {
            const refreshedExternalAssignments = await refetchExternalUserFormAssignments();

            nextSettings.externalUserEmailAssignments = (refreshedExternalAssignments.data ?? []).map((item) => ({
                id: item.id,
                email: item.email
            })).sort((a, b) => getRecipientSortLabel(a).localeCompare(getRecipientSortLabel(b), undefined, {sensitivity: 'base'}));
        }

        if (audience === FORM_AUDIENCE_TYPE.employees) {
            if (effectiveLinkedToStudents) {
                const refreshedLinkedAssignments = await refetchLinkedStudentAssignments();
                const linkedAssignmentData = refreshedLinkedAssignments.data ?? {
                    personAssignments: [],
                    groupFilters: []
                };
                const linkedRecipientFilters = await buildStudentRecipientFilters(
                    linkedAssignmentData.personAssignments ?? []
                );

                Object.assign(nextSettings, {
                    linkedGroupFilters: mapGroupFilters(linkedAssignmentData.groupFilters ?? []),
                    linkedStudentFilters: linkedRecipientFilters.studentFilters,
                    linkedApplicationFilters: linkedRecipientFilters.applicationFilters,
                    linkedEnquiryFilters: linkedRecipientFilters.enquiryFilters,
                    linkedEmployeeFilters: [],
                    linkedStrictFiltering: !!privateFormSettings.linkedStrictFiltering
                });
            } else if (effectiveLinkedToEmployees) {
                const refreshedLinkedAssignments = await refetchLinkedEmployeeAssignments();
                const linkedAssignmentData = refreshedLinkedAssignments.data ?? {
                    personAssignments: [],
                    groupFilters: []
                };

                Object.assign(nextSettings, {
                    linkedGroupFilters: mapGroupFilters(linkedAssignmentData.groupFilters ?? []),
                    linkedEmployeeFilters: sortRecipientFilters((linkedAssignmentData.personAssignments ?? []).map((item) => ({
                        id: item.personId,
                        assignmentId: item.id,
                        label: item.displayName || item.name,
                        preferredForename: item.displayName || item.name,
                        preferredSurname: '',
                    }))),
                    linkedStudentFilters: [],
                    linkedApplicationFilters: [],
                    linkedEnquiryFilters: [],
                    linkedStrictFiltering: !!privateFormSettings.linkedStrictFiltering
                });
            } else {
                Object.assign(nextSettings, {
                    linkedGroupFilters: [],
                    linkedStudentFilters: [],
                    linkedApplicationFilters: [],
                    linkedEnquiryFilters: [],
                    linkedEmployeeFilters: [],
                    linkedStrictFiltering: false
                });
            }
        }

        const normalizedRecipientSettings = getRecipientSettingsSnapshot({
            ...privateFormSettings,
            ...nextSettings
        });

        setOriginalRecipientSettings(normalizedRecipientSettings);
        setPrivateFormSettings(prev => ({
            ...prev,
            ...normalizedRecipientSettings,
            validation: {
                ...prev.validation,
                ...validateGroupFilters(normalizedRecipientSettings.groupFilters),
                ...validateAccessLevel(accessLevel, normalizedRecipientSettings.groupFilters)
            }
        }));
    };

    const setAccessLevelRules = (nextAccessLevel, nextAudience) => {
        const enforcedAccessLevel =
            nextAudience === FORM_AUDIENCE_TYPE.employees
                ? FORM_ACCESS_LEVELS.private
                : nextAccessLevel;

        setAccessLevel(enforcedAccessLevel);

        return enforcedAccessLevel;
    };

    const modifyAccessLevelByTemplateAndAudience = (value, audienceArg) => {
        setAccessLevelRules(value, audienceArg);
    };

    /**
     * The server counts a form whose components are all Notes or the school logo as a
     * communication, and clears every linked setting when it saves one. This is the same rule,
     * so a school can be told before that happens rather than afterwards.
     */
    const becomesCommunicationOnSave = useMemo(() => becomesCommunication(schema), [schema]);

    const settingsLostOnSave = useMemo(() => {
        if (!becomesCommunicationOnSave) {
            return [];
        }

        const lost = [];

        if (baseLinkedToStudents) {
            lost.push('the linked student');
        }

        if (baseLinkedToEmployees) {
            lost.push('the linked employee');
        }

        if (privateFormSettings?.linkedStrictFiltering) {
            lost.push('the restriction to the linked person’s own groups');
        }

        if (allLinkedEmployeesCanSubmit) {
            lost.push('All linked employees can submit');
        }

        if (showResponsesInLinkedProfile) {
            lost.push('Show submitted responses on the linked person’s profile');
        }

        if (showResponsesToLinkedParents) {
            lost.push('Show responses to linked parents');
        }

        if (allowMultipleSubmissions) {
            lost.push('Self-service');
        }

        return lost;
    }, [
        becomesCommunicationOnSave,
        allowMultipleSubmissions,
        baseLinkedToStudents,
        baseLinkedToEmployees,
        privateFormSettings?.linkedStrictFiltering,
        allLinkedEmployeesCanSubmit,
        showResponsesInLinkedProfile,
        showResponsesToLinkedParents
    ]);

    const canSwitchToEmail = useMemo(() => {
        if (!Array.isArray(schema) || schema.length === 0) {
            return false;
        }

        const contentComponents = schema.filter(
            (component) => component?.type !== 'schoolLogo'
        );

        if (contentComponents.length !== 1) {
            return false;
        }

        const onlyComponent = contentComponents[0];
        return onlyComponent?.type === 'additionalNotes';
    }, [schema]);

    useEffect(() => {
        if (!formId) return;
        if (isWizardMode) return;

        getForm(formId)
            .then(({data: form}) => {
                setFormId(form.id);
                setSchema(form.schema);
                setName(form.name);
                setFormStatus(form.status);
                setRemindInterval(form.remindInterval);
                setExpirationDate(form.expirationDate);
                setCategories(form.categories?.map(x => x.id) ?? []);
                setStrictRecipientsFiltering(form.strictRecipientsFiltering);
                setAllParentsCanSubmit(form.allParentsCanSubmit);
                setSendToPriority1ContactsOnly(!!form.sendToPriority1ContactsOnly);
                setVisibleToParents(form.visibleToParents ?? true);
                setShareResponseWithOtherParents(form.shareResponseWithOtherParents ?? true);
                setShowResponsesInLinkedProfile(form.showResponsesInLinkedProfile ?? false);
                setShowResponsesToLinkedParents(form.showResponsesToLinkedParents ?? false);
                setProfileMustBeComplete(form.profileMustBeComplete);
                setAllowMultipleSubmissions(form.allowMultipleSubmissions);
                setEnableRecipientNotifications(form.enableRecipientNotifications ?? true);
                const resolvedParentAudienceType = normalizeLegacyParentAudienceType({
                    audience: form.audience,
                    parentAudienceType: form.parentAudienceType,
                    accessLevel: form.accessLevel,
                    templateType: form.template?.type,
                });
                const shouldInferLegacyParentAudience =
                    Number(form.audience) === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
                    Number(form.parentAudienceType ?? PARENT_AUDIENCE_TYPE.none) === PARENT_AUDIENCE_TYPE.none;
                const resolvedAudience = resolveFrontendAudience(form.audience, resolvedParentAudienceType);
                setSelectedAudience(resolvedAudience);
                setLocalParentAudienceType(resolvedParentAudienceType);
                setShouldInferLegacyParentAudienceType(shouldInferLegacyParentAudience);
                setAccessLevelRules(form.accessLevel, resolvedAudience);
                setTemplate(form.template);
                setIsCommunicationForm(form.isCommunication);
                setNotificationEmails(form.notificationEmails?.map(email => ({email})) || []);
                setLinkToStudents(!!(form.linkToStudents ?? form.LinkToStudents));
                setLinkToEmployees(!!(form.linkToEmployees ?? form.LinkToEmployees));
                setAllLinkedEmployeesCanSubmit(
                    !!(form.allLinkedEmployeesCanSubmit ?? form.AllLinkedEmployeesCanSubmit)
                );

                setPrivateFormSettings(prev => ({
                    ...prev,
                    assignmentType: form.assignmentType,
                    scheduledActivationAt: form.scheduledActivationAt,
                    linkedStrictFiltering: !!form.linkedStrictFiltering,
                    allLinkedEmployeesCanSubmit:
                        !!(form.allLinkedEmployeesCanSubmit ?? form.AllLinkedEmployeesCanSubmit),
                }));
                setOriginalRecipientSettings(prev => ({
                    ...prev,
                    assignmentType: form.assignmentType,
                    linkedStrictFiltering: !!form.linkedStrictFiltering
                }));

                setEnableAutoresponder(form.enableAutoresponder);
            })
            .catch(() => {
                showErrorNotification('Failed to retrieve form data');
            });
    }, [formId, isWizardMode, getForm]);

    useEffect(() => {
        if (!template) return;

        const isTransitionForm = template.type === FormTemplateType.transition;
        const isOfferForm = template.type === FormTemplateType.offer;
        const isPublicForm = template.type === FormTemplateType.acceptance ||
            template.type === FormTemplateType.application ||
            template.type === FormTemplateType.shareable;
        const includeEnquiryAudience =
            audience === FORM_AUDIENCE_TYPE.enquiries ||
            currentSchool?.disableEnquiriesSync === false;
        const offerAudienceOptions = includeEnquiryAudience
            ? [
                ...audienceOptionsByFormTemplateType.offer,
                {
                    value: FORM_AUDIENCE_TYPE.enquiries,
                    label: 'Enquiry'
                }
            ]
            : audienceOptionsByFormTemplateType.offer;

        const audienceOptionsLocal = isTransitionForm
            ? audienceOptionsByFormTemplateType.transition
            : isOfferForm
                ? offerAudienceOptions
                : getDefaultAudienceOptions(includeEnquiryAudience);

        const accessLevelLocal = isTransitionForm || isOfferForm
            ? FORM_ACCESS_LEVELS.publicWithStudentLink
            : isPublicForm
                ? FORM_ACCESS_LEVELS.public
                : FORM_ACCESS_LEVELS.private;

        if (!isEditMode) {
            if (audienceOptionsLocal.length === 1) {
                const audienceOption = audienceOptionsLocal[0];
                modifyAccessLevelByTemplateAndAudience(accessLevelLocal, audienceOption.value);
            } else {
                setAccessLevel(accessLevelLocal);
            }

            if (template?.schema) {
                const value = [...template.schema, schoolLogoSchemaComponent];
                setSchema(value);
            }
        }

        setAudienceOptions(audienceOptionsLocal);
    }, [
        audience,
        currentSchool?.disableEnquiriesSync,
        isEditMode,
        schoolLogoSchemaComponent,
        template?.id,
        template?.type
    ]);

    useEffect(() => {
        setHasInitialisedGroupFilters(false);
        setHasInitialisedStudentRecipients(false);
        setHasInitialisedEmployeeRecipients(false);
        setOriginalRecipientSettings(prev => ({
            ...prev,
            groupFilters: [],
            studentFilters: [],
            applicationFilters: [],
            enquiryFilters: [],
            employeeFilters: [],
            externalUserEmailAssignments:
                Number(audience) === FORM_AUDIENCE_TYPE.externalUsers
                    ? prev.externalUserEmailAssignments
                    : [],
            linkedGroupFilters: [],
            linkedStudentFilters: [],
            linkedApplicationFilters: [],
            linkedEnquiryFilters: [],
        }));
    }, [
        formId,
        audience,
        privateFormSettings.assignmentType,
        assignmentTypeForRecipients
    ]);

    useEffect(() => {
        setHasInitialisedExternalRecipients(false);
        setOriginalRecipientSettings(prev => ({
            ...prev,
            externalUserEmailAssignments:
                Number(audience) === FORM_AUDIENCE_TYPE.externalUsers
                    ? prev.externalUserEmailAssignments
                    : [],
        }));
    }, [
        formId,
        audience
    ]);

    useEffect(() => {
        setHasInitialisedLinkedStudents(false);
    }, [
        formId,
        effectiveLinkedToStudents
    ]);

    useEffect(() => {
        setHasInitialisedLinkedEmployees(false);
    }, [
        formId,
        effectiveLinkedToEmployees
    ]);

    useEffect(() => {
        setShouldInferLegacyParentAudienceType(false);
    }, [formId]);

    useEffect(() => {
        if (!formId) return;
        if (enableTrigger) return;
        if (hasInitialisedGroupFilters) return;

        const assignmentsLoaded =
            assignmentTypeForRecipients === FORM_ASSIGNMENT_TYPE.employees
                ? isFetchEmployeeFormAssignmentSuccess
                : isFetchStudentFormAssignmentSuccess;

        if (!assignmentsLoaded) return;

        const raw = effectiveAssignments.groupFilters || [];
        const groupFilters = raw.map((x) => ({
            id: x.filterId,
            displayName: x.displayName,
            groupId: x.id,
            excluded: x.excluded,
            deleted: x.deleted,
        }));

        setPrivateFormSettings((prev) => ({
            ...prev,
            groupFilters,
            validation: {
                ...validateGroupFilters(groupFilters),
                ...validateAccessLevel(accessLevel, groupFilters),
            },
        }));
        setOriginalRecipientSettings((prev) => ({
            ...prev,
            groupFilters
        }));

        setHasInitialisedGroupFilters(true);
    }, [
        formId,
        enableTrigger,
        hasInitialisedGroupFilters,
        assignmentTypeForRecipients,
        effectiveAssignments.groupFilters,
        isFetchEmployeeFormAssignmentSuccess,
        isFetchStudentFormAssignmentSuccess,
        accessLevel,
    ]);

    useEffect(() => {
        if (!formId) return;
        if (enableTrigger) return;
        if (privateFormSettings.assignmentType !== FORM_ASSIGNMENT_TYPE.students) return;
        if (hasInitialisedStudentRecipients) return;
        if (!isFetchStudentFormAssignmentSuccess) return;

        const lookupsReady = studentIds.length === 0 || pupilsData !== undefined;
        if (!lookupsReady) return;

        const filters = buildPupilFilters(pupilsData ?? [], studentFormAssignmentData);

        setPrivateFormSettings(prev => ({
            ...prev,
            ...filters
        }));
        setOriginalRecipientSettings(prev => ({
            ...prev,
            ...filters,
            assignmentType: FORM_ASSIGNMENT_TYPE.students
        }));

        setHasInitialisedStudentRecipients(true);
    }, [
        formId,
        enableTrigger,
        privateFormSettings.assignmentType,
        hasInitialisedStudentRecipients,
        isFetchStudentFormAssignmentSuccess,
        studentFormAssignmentData,
        studentIds.length,
        pupilsData,
    ]);

    useEffect(() => {
        if (!formId) return;
        if (enableTrigger) return;
        if (privateFormSettings.assignmentType !== FORM_ASSIGNMENT_TYPE.employees) return;
        if (hasInitialisedEmployeeRecipients) return;
        if (!isFetchEmployeeFormAssignmentSuccess) return;

        const personAssignments = employeeFormAssignmentData?.personAssignments || [];
        const employeeFilters = personAssignments.map((item) => ({
            id: item.personId,
            assignmentId: item.id,
            label: item.displayName,
        }));
        const sortedEmployeeFilters = sortRecipientFilters(employeeFilters);

        setPrivateFormSettings((prev) => ({
            ...prev,
            employeeFilters: sortedEmployeeFilters,
        }));
        setOriginalRecipientSettings((prev) => ({
            ...prev,
            employeeFilters: sortedEmployeeFilters,
            assignmentType: FORM_ASSIGNMENT_TYPE.employees
        }));

        setHasInitialisedEmployeeRecipients(true);
    }, [
        formId,
        enableTrigger,
        privateFormSettings.assignmentType,
        hasInitialisedEmployeeRecipients,
        isFetchEmployeeFormAssignmentSuccess,
        employeeFormAssignmentData,
    ]);

    useEffect(() => {
        if (!formId) return;
        if (audience !== FORM_AUDIENCE_TYPE.externalUsers) return;
        if (hasInitialisedExternalRecipients) return;
        if (externalUserFormAssignments === undefined) return;

        setPrivateFormSettings((prev) => ({
            ...prev,
            externalUserEmailAssignments: sortRecipientFilters((externalUserFormAssignments || []).map((x) => ({
                id: x.id,
                email: x.email,
            }))),
        }));
        setOriginalRecipientSettings((prev) => ({
            ...prev,
            externalUserEmailAssignments: sortRecipientFilters((externalUserFormAssignments || []).map((x) => ({
                id: x.id,
                email: x.email,
            })))
        }));

        setHasInitialisedExternalRecipients(true);
    }, [
        formId,
        audience,
        enableTrigger,
        hasInitialisedExternalRecipients,
        externalUserFormAssignments,
    ]);

    useEffect(() => {
        let cancelled = false;

        const clearTrigger = () => {
            setEnableTrigger(false);
            setPrivateFormSettings(prev => ({
                ...prev,
                triggerFormSettings: {
                    selectedForm: null,
                    selectedFormSchema: null,
                    relatedComponent: null,
                    targetState: null,
                    relatedFormEmailFieldId: null,
                    relatedFormEmployeeFieldId: null
                }
            }));
        };

        if (!formQuestionAssigment?.id) {
            clearTrigger();
            return () => {
                cancelled = true;
            };
        }

        getTriggerForm(Number(formQuestionAssigment.relatedFormId))
            .unwrap()
            .then((form) => {
                if (cancelled) return;
                if (!form) throw new Error('Trigger form payload was empty');

                const relatedComponent =
                    form.schema?.find(e => e.id === formQuestionAssigment.relatedComponentId) ?? null;

                const triggerSettings = {
                    id: formQuestionAssigment.id,
                    selectedForm: {
                        id: Number(formQuestionAssigment.relatedFormId),
                        name: form.name,
                        schema: form.schema,
                        audience: form.audience,
                        assignmentType: form.assignmentType,
                    },
                    relatedComponent,
                    targetState: formQuestionAssigment.targetState,
                    relatedFormEmailFieldId: formQuestionAssigment.relatedFormEmailFieldId ?? null,
                    relatedFormEmployeeFieldId: formQuestionAssigment.relatedFormEmployeeFieldId ?? null,
                };

                setPrivateFormSettings(prev => ({
                    ...prev,
                    triggerFormSettings: {
                        ...triggerSettings,
                        original: triggerSettings
                    }
                }));

                setEnableTrigger(true);
            })
            .catch(() => {
                if (cancelled) return;
                clearTrigger();
            });

        return () => {
            cancelled = true;
        };
    }, [
        formQuestionAssigment?.id,
        formQuestionAssigment?.relatedFormId,
        formQuestionAssigment?.relatedComponentId,
        formQuestionAssigment?.targetState,
        formQuestionAssigment?.relatedFormEmailFieldId,
        formQuestionAssigment?.relatedFormEmployeeFieldId,
        getTriggerForm,
    ]);

    useEffect(() => {
        if (!formId) return;
        if (enableTrigger) return;
        if (hasInitialisedLinkedStudents) return;
        if (!shouldFetchLinkedStudents) return;
        if (!isFetchLinkedStudentAssignmentsSuccess) return;
        if (!linkedStudentAssignmentData) return;

        const linkedGroupFilters = (linkedStudentAssignmentData.groupFilters || []).map((x) => ({
            id: x.filterId,
            displayName: x.displayName,
            groupId: x.id,
            excluded: x.excluded,
            deleted: x.deleted,
        }));

        const lookupsReady = linkedStudentIds.length === 0 || linkedPupilsData !== undefined;
        if (!lookupsReady) return;

        const linkedFilters = buildPupilFilters(linkedPupilsData ?? [], linkedStudentAssignmentData);

        setPrivateFormSettings(prev => ({
            ...prev,
            linkedGroupFilters,
            linkedStudentFilters: linkedFilters.studentFilters,
            linkedApplicationFilters: linkedFilters.applicationFilters,
            linkedEnquiryFilters: linkedFilters.enquiryFilters,
        }));
        setOriginalRecipientSettings(prev => ({
            ...prev,
            linkedGroupFilters,
            linkedStudentFilters: linkedFilters.studentFilters,
            linkedApplicationFilters: linkedFilters.applicationFilters,
            linkedEnquiryFilters: linkedFilters.enquiryFilters,
            linkedStrictFiltering: !!privateFormSettings.linkedStrictFiltering
        }));

        setHasInitialisedLinkedStudents(true);
    }, [
        formId,
        enableTrigger,
        hasInitialisedLinkedStudents,
        shouldFetchLinkedStudents,
        isFetchLinkedStudentAssignmentsSuccess,
        linkedStudentAssignmentData,
        linkedStudentIds.length,
        linkedPupilsData,
    ]);

    useEffect(() => {
        if (!formId) return;
        if (enableTrigger) return;
        if (!shouldFetchLinkedEmployees) return;
        if (hasInitialisedLinkedEmployees) return;
        if (!isFetchLinkedEmployeeAssignmentsSuccess) return;
        if (!linkedEmployeeAssignmentData) return;

        const linkedGroupFilters = (linkedEmployeeAssignmentData.groupFilters || []).map((x) => ({
            id: x.filterId,
            displayName: x.displayName,
            groupId: x.id,
            excluded: x.excluded,
            deleted: x.deleted,
        }));

        const linkedEmployeeFilters = sortRecipientFilters(
            (linkedEmployeeAssignmentData.personAssignments || []).map((item) => ({
                id: item.personId,
                assignmentId: item.id,
                label: item.displayName || item.name,
                preferredForename: item.displayName || item.name,
                preferredSurname: '',
            }))
        );

        setPrivateFormSettings(prev => ({
            ...prev,
            linkedGroupFilters,
            linkedEmployeeFilters,
        }));
        setOriginalRecipientSettings(prev => ({
            ...prev,
            linkedGroupFilters,
            linkedEmployeeFilters,
            linkedStrictFiltering: !!privateFormSettings.linkedStrictFiltering
        }));

        setHasInitialisedLinkedEmployees(true);
    }, [
        formId,
        enableTrigger,
        hasInitialisedLinkedEmployees,
        shouldFetchLinkedEmployees,
        isFetchLinkedEmployeeAssignmentsSuccess,
        linkedEmployeeAssignmentData,
    ]);

    const isTriggerForm = enableTrigger && !!privateFormSettings.triggerFormSettings?.selectedForm;

    const hasInitialisedRecipients =
        hasInitialisedGroupFilters &&
        (privateFormSettings.assignmentType !== FORM_ASSIGNMENT_TYPE.students || hasInitialisedStudentRecipients) &&
        (privateFormSettings.assignmentType !== FORM_ASSIGNMENT_TYPE.employees || hasInitialisedEmployeeRecipients) &&
        (audience !== FORM_AUDIENCE_TYPE.externalUsers || hasInitialisedExternalRecipients) &&
        (!shouldFetchLinkedStudents || hasInitialisedLinkedStudents) &&
        (!shouldFetchLinkedEmployees || hasInitialisedLinkedEmployees);

    useEffect(() => {
        if (!formId) return;
        if (isWizardMode) return;
        if (!shouldInferLegacyParentAudienceType) return;
        if (!hasInitialisedRecipients) return;

        if (hasLegacyEnquiryEvidence(privateFormSettings)) {
            setSelectedAudience(FORM_AUDIENCE_TYPE.enquiries);
            setLocalParentAudienceType(PARENT_AUDIENCE_TYPE.enquiry);
            setAccessLevelRules(FORM_ACCESS_LEVELS.publicWithStudentLink, FORM_AUDIENCE_TYPE.enquiries);
        }

        setShouldInferLegacyParentAudienceType(false);
    }, [
        formId,
        isWizardMode,
        shouldInferLegacyParentAudienceType,
        hasInitialisedRecipients,
        privateFormSettings,
    ]);

    const hasRecipients = useMemo(() => {
        if (isTriggerForm) return true;

        const s = privateFormSettings;
        const hasGroups = s.groupFilters?.some(x => !x.isDeleted && !x.deleted);

        const hasStudents = [
            ...(s.studentFilters || []),
            ...(s.applicationFilters || []),
            ...(s.enquiryFilters || []),
        ].some(x => !x.isDeleted && !x.deleted);

        const hasEmployees = (s.employeeFilters || []).some(x => !x.isDeleted && !x.deleted);
        const hasExternal = (s.externalUserEmailAssignments || []).some(x => !x.isDeleted && !x.deleted);

        return !!(hasGroups || hasStudents || hasEmployees || hasExternal);
    }, [isTriggerForm, privateFormSettings]);

    const handleRemindIntervalChange = (value) => {
        const nextValue = parseRemindIntervalInput(value);
        if (nextValue === null) {
            return;
        }
        setRemindInterval(nextValue);
    };

    const isArchived = formStatus === FORM_STATUSES.archived;
    const isExpired = formStatus === FORM_STATUSES.expired;
    const isInactive = formStatus === FORM_STATUSES.inactive;
    const isReadOnlyForm = isArchived || isExpired;
    const isPublicTemplateForm =
        template?.type === 'shareable' ||
        template?.type === 'application';
    const publicSettingsFormType = isWizardMode
        ? (wizardFormType ?? null)
        : (
            template?.type === FormTemplateType.shareable
                ? FORM_TYPE.shareable
                : (
                    template?.type === FormTemplateType.application ||
                    template?.type === FormTemplateType.acceptance
                )
                    ? FORM_TYPE.application
                    : null
        );
    const expirationMoment = expirationDate ? moment(expirationDate) : null;
    const isExpirationInPast = !!expirationMoment && moment().isAfter(expirationMoment);
    const showGoLiveActions = isInactive && !!formId;
    const hasExistingScheduledGoLive = !!privateFormSettings.scheduledActivationAt;
    const canMakeLiveWithoutRecipients = isPublicTemplateForm || isTriggerForm;

    const canSaveForm =
        (
            formStatus == null ||
            formStatus === FORM_STATUSES.inactive ||
            formStatus === FORM_STATUSES.active ||
            formStatus === FORM_STATUSES.expired ||
            formStatus === FORM_STATUSES.archived
        );

    const handleNameChange = (event) => setName(event.target.value);

    useEffect(() => {
        setScheduledGoLiveDate(
            privateFormSettings.scheduledActivationAt
                ? moment(privateFormSettings.scheduledActivationAt)
                : null
        );
        setScheduledGoLiveDateError('');
    }, [privateFormSettings.scheduledActivationAt]);

    useEffect(() => {
        const ENTER_THRESHOLD = 220;
        const EXIT_THRESHOLD = 320;

        const handleScroll = () => {
            const doc = document.documentElement;
            const scrollTop = doc.scrollTop || document.body.scrollTop;
            const scrollHeight = doc.scrollHeight || document.body.scrollHeight;
            const clientHeight = doc.clientHeight;

            const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

            setIsAtBottom(prev => {
                if (!prev && distanceFromBottom < ENTER_THRESHOLD) return true;
                if (prev && distanceFromBottom > EXIT_THRESHOLD) return false;
                return prev;
            });
        };

        window.addEventListener('scroll', handleScroll, {passive: true});
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const saveFormAndAccessSettings = async (skipAssignmentsSave = false) => {
        const saved = await saveFormChanges(skipAssignmentsSave);

        if (!saved) {
            return false;
        }

        if (formAccessRef.current) {
            try {
                await formAccessRef.current.saveAccessSettings({suppressSuccessNotification: true});
            } catch {
                showErrorNotification('Failed to save form access settings');
                return false;
            }
        }

        return true;
    };

    const getFromTabIndex = () =>
        typeof location.state?.fromTabIndex === 'number'
            ? location.state.fromTabIndex
            : 1;

    const handleExitWithoutSave = () => {
        const fromTabIndex = getFromTabIndex();
        navigate('/forms', {state: {activeTabIndex: fromTabIndex}});
    };

    const handleSaveForm = ({
        targetFormId = formId,
        targetTemplateId = template?.id,
        targetAudience,
        scheduledActivationAtOverride,
        isCommunicationOverride,
    } = {}) => {
        const isCommunicationValue =
            typeof isCommunicationOverride === 'boolean'
                ? isCommunicationOverride
                : isCommunicationForm;
        const persistedLinkToStudents = !isCommunicationValue && baseLinkedToStudents;
        const persistedLinkToEmployees = !isCommunicationValue && baseLinkedToEmployees;
        const persistedAllLinkedEmployeesCanSubmit =
            (persistedLinkToStudents || persistedLinkToEmployees) && !!allLinkedEmployeesCanSubmit;
        const persistedLinkedStrictFiltering =
            (persistedLinkToStudents || persistedLinkToEmployees) &&
            !!privateFormSettings.linkedStrictFiltering;
        const resolvedAudience = targetAudience ?? (
            audienceOptions.length === 1
                ? audienceOptions[0].value
                : audience
        );
        const resolvedParentAudienceType = normalizeLegacyParentAudienceType({
            audience: resolvedAudience,
            parentAudienceType,
            accessLevel,
            templateType: template?.type,
        });
        const persistedAudiencePayload =
            resolveBackendAudiencePayload(resolvedAudience, resolvedParentAudienceType);

        return {
            ...(targetFormId != null ? {id: targetFormId} : {templateId: targetTemplateId}),
            schema,
            name,
            remindInterval,
            categoryIds: categories,
            strictRecipientsFiltering,
            allParentsCanSubmit,
            sendToPriority1ContactsOnly,
            visibleToParents,
            shareResponseWithOtherParents,
            showResponsesInLinkedProfile,
            showResponsesToLinkedParents,
            allowMultipleSubmissions,
            enableRecipientNotifications,
            audience: persistedAudiencePayload.audience,
            parentAudienceType: persistedAudiencePayload.parentAudienceType,
            accessLevel,
            expirationDate,
            notificationEmails: notificationEmails.map(value => value.email),
            assignmentType: privateFormSettings.assignmentType ?? 0,
            enableAutoresponder,
            scheduledActivationAt:
                scheduledActivationAtOverride !== undefined
                    ? scheduledActivationAtOverride
                    : privateFormSettings.scheduledActivationAt,
            isCommunication: isCommunicationValue,
            profileMustBeComplete,
            linkToStudents: persistedLinkToStudents,
            linkToEmployees: persistedLinkToEmployees,
            allLinkedEmployeesCanSubmit: persistedAllLinkedEmployeesCanSubmit,
            linkedStrictFiltering: persistedLinkedStrictFiltering,
        };
    };

    const saveFormChanges = async (skipAssignmentsSave = false, isCommunicationOverride) => {
        setComponentToEditIndex(-1);

        let targetFormId = formId;

        if (isEditMode || formId != null) {
            const response = await saveForm(handleSaveForm({
                targetFormId: formId,
                isCommunicationOverride,
            }));

            if (response.error) {
                showErrorNotification('Failed to save form');
                return false;
            }
        } else {
            const formAudience = audienceOptions.length === 1
                ? audienceOptions[0].value
                : audience;

            const response = await saveForm(handleSaveForm({
                targetFormId: null,
                targetTemplateId: template.id,
                targetAudience: formAudience,
                isCommunicationOverride,
            }));

            if (response.error) {
                showErrorNotification('Failed to save form');
                return false;
            }

            const savedFormId = response.data?.formId;

            if (!savedFormId) {
                showErrorNotification('Form save did not return a form id');
                return false;
            }

            targetFormId = savedFormId;
            setFormId(savedFormId);
            setSelectedAudience(formAudience);
        }

        try {
            if (!skipAssignmentsSave) {
                await handleSaveFormSettings(targetFormId);
            }

            showSuccessNotification('Form Saved Successfully!');
            return true;
        } catch (error) {
            if (error.type === 'SaveFormSettingsError') {
                showDetailedErrorNotification('Failed to save form settings', error.details);
            } else {
                showErrorNotification('Failed to save form');
            }

            return false;
        }
    };

    const becomingCommunicationMessage = (action) =>
        communicationWarning(action, settingsLostOnSave);

    const confirmSave = (func) => {
        setComponentToEditIndex(-1);

        const becomingCommunication = settingsLostOnSave.length > 0;

        confirm({
            title: becomingCommunication ? 'This will become an email communication' : 'Confirmation',
            description: becomingCommunication
                ? becomingCommunicationMessage('saving it')
                : 'Are you sure you want to save the changes and proceed?',
            confirmationText: becomingCommunication ? 'Save anyway' : 'Save',
        })
            .then(func)
            .catch(() => {
            });
    };

    /**
     * Going live saves the form on its way, so it loses the linked settings in the same way a
     * save does. It never passed through confirmSave, so a school lost the linked employee
     * without being told.
     */
    const confirmGoLive = async () => {
        if (settingsLostOnSave.length === 0) {
            return true;
        }

        setComponentToEditIndex(-1);

        try {
            await confirm({
                title: 'This will become an email communication',
                description: becomingCommunicationMessage('making it live'),
                confirmationText: 'Make it live anyway',
            });

            return true;
        } catch {
            return false;
        }
    };

    const handleSaveFormSettings = async (targetFormId = formId) => {
        const classifyFilters = (filters) => {
            return (filters || []).reduce(
                (acc, filter) => {
                    filter.isDeleted && !filter.isNew
                        ? acc.deleted.push(filter)
                        : filter.isNew && acc.created.push(filter);
                    return acc;
                },
                {
                    created: [],
                    deleted: []
                }
            );
        };

        const studentFilters = classifyFilters([
            ...privateFormSettings.studentFilters,
            ...privateFormSettings.applicationFilters,
            ...privateFormSettings.enquiryFilters
        ]);

        const groupFilters = classifyFilters(privateFormSettings.groupFilters);
        const employeeFilters = classifyFilters(privateFormSettings.employeeFilters);
        const externalUserFormAssignments = classifyFilters(privateFormSettings.externalUserEmailAssignments);

        const tasks = [];

        const createdGroupFilters = groupFilters.created.length
            ? groupFilters.created.map(x => ({
                filterId: x.id,
                excluded: x.excluded
            }))
            : [];

        const personAssignments =
            studentFilters.created.length
                ? studentFilters.created.map(x => ({
                    personId: x.id,
                    name: x.label
                }))
                : employeeFilters.created.length
                    ? employeeFilters.created.map(x => ({
                        personId: x.id,
                        name: x.label || `${x.preferredForename} ${x.preferredSurname}`
                    }))
                    : [];

        if (personAssignments.length || createdGroupFilters.length) {
            const func = {
                [FORM_ASSIGNMENT_TYPE.students]: createFormStudentAssignments,
                [FORM_ASSIGNMENT_TYPE.employees]: createFormEmployeeAssignment,
            }[privateFormSettings.assignmentType] ?? (() => {
            });

            tasks.push({
                action: () => func({
                    formId: targetFormId,
                    personAssignments,
                    groupFilters: createdGroupFilters
                }),
                label: 'creating person + group filters'
            });
        }

        if (groupFilters.deleted.length) {
            tasks.push({
                action: () => deleteFormAssignments({
                    formId: targetFormId,
                    formAssignmentIds: groupFilters.deleted.map(x => x.groupId)
                }),
                label: 'deleting group filters'
            });
        }

        if (studentFilters.deleted.length) {
            tasks.push({
                action: () => deleteFormStudentsAssignments({
                    formId: targetFormId,
                    ids: studentFilters.deleted.map(x => x.assignmentId)
                }),
                label: 'deleting student filters'
            });
        }

        if (externalUserFormAssignments.deleted.length) {
            tasks.push({
                action: () => deleteExternalUserFormAssignments({
                    formId: targetFormId,
                    assignmentIds: externalUserFormAssignments.deleted.map(x => x.id)
                }),
                label: 'deleting external user form assignments'
            });
        }

        const activeExternalUserEmails = (privateFormSettings.externalUserEmailAssignments ?? [])
            .filter((x) => !x.isDeleted && !x.deleted)
            .map((x) => x.email)
            .filter(Boolean);

        if (Number(audience) === FORM_AUDIENCE_TYPE.externalUsers && activeExternalUserEmails.length) {
            tasks.push({
                action: () => createExternalUserFormAssignments({
                    formId: targetFormId,
                    emails: activeExternalUserEmails
                }),
                label: 'creating external user form assignments'
            });
        }

        if (employeeFilters.deleted.length) {
            tasks.push({
                action: () => deleteFormStudentsAssignments({
                    formId: targetFormId,
                    ids: employeeFilters.deleted.map(x => x.assignmentId)
                }),
                label: 'deleting employee filters'
            });
        }

        if (privateFormSettings.triggerFormSettings) {
            const {
                selectedForm,
                original,
                relatedComponent,
                targetState
            } = privateFormSettings.triggerFormSettings;

            if (!selectedForm && original) {
                tasks.push({
                    action: () => deleteFormQuestionAssignments({
                        id: original.id,
                        formId: targetFormId
                    }),
                    label: 'deleting form trigger'
                });
            }

            const sanitizedTriggerFields = sanitizeTriggerRecipientFieldIds(
                audience,
                privateFormSettings.triggerFormSettings
            );
            const relatedFormEmailFieldId = sanitizedTriggerFields.relatedFormEmailFieldId ?? null;
            const originalRelatedFormEmailFieldId = original?.relatedFormEmailFieldId ?? null;
            const relatedFormEmployeeFieldId = sanitizedTriggerFields.relatedFormEmployeeFieldId ?? null;
            const originalRelatedFormEmployeeFieldId = original?.relatedFormEmployeeFieldId ?? null;

            if (selectedForm && (
                original == null ||
                selectedForm.id !== original.selectedForm.id ||
                relatedComponent.id !== original.relatedComponent.id ||
                targetState !== original.targetState ||
                relatedFormEmailFieldId !== originalRelatedFormEmailFieldId ||
                relatedFormEmployeeFieldId !== originalRelatedFormEmployeeFieldId
            )) {
                const body = {
                    formId: targetFormId,
                    targetState: typeof targetState === 'string' ? targetState : `${targetState}`,
                    relatedComponentId: relatedComponent.id,
                    relatedFormId: selectedForm.id,
                    displayName: `${selectedForm.name} - ${relatedComponent.description}`
                };
                if (relatedFormEmailFieldId) {
                    body.relatedFormEmailFieldId = relatedFormEmailFieldId;
                }
                if (relatedFormEmployeeFieldId) {
                    body.relatedFormEmployeeFieldId = relatedFormEmployeeFieldId;
                }
                tasks.push({
                    action: () => createFormQuestionAssignment(body),
                    label: 'creating form trigger'
                });
            }
        }

        if (audience === FORM_AUDIENCE_TYPE.employees) {
            const linkedStudentFilters = classifyFilters([
                ...(privateFormSettings.linkedStudentFilters || []),
                ...(privateFormSettings.linkedApplicationFilters || []),
                ...(privateFormSettings.linkedEnquiryFilters || [])
            ]);

            const linkedGroupFilters = classifyFilters(privateFormSettings.linkedGroupFilters || []);

            const createdLinkedGroupFilters = linkedGroupFilters.created.length
                ? linkedGroupFilters.created.map(x => ({
                    filterId: x.id,
                    excluded: x.excluded
                }))
                : [];

            const linkedPersonAssignments = linkedStudentFilters.created.map(x => ({
                personId: x.id,
                name: x.label
            }));

            if (effectiveLinkedToStudents === true && (linkedPersonAssignments.length || createdLinkedGroupFilters.length)) {
                tasks.push({
                    action: () => createFormStudentAssignments({
                        formId: targetFormId,
                        personAssignments: linkedPersonAssignments,
                        groupFilters: createdLinkedGroupFilters,
                        linkedStudent: true
                    }),
                    label: 'creating linked student associations'
                });
            }

            if (linkedGroupFilters.deleted.length && effectiveLinkedToStudents === true) {
                tasks.push({
                    action: () => deleteFormAssignments({
                        formId: targetFormId,
                        formAssignmentIds: linkedGroupFilters.deleted.map(x => x.groupId),
                        linkedStudent: true
                    }),
                    label: 'deleting linked group filters'
                });
            }

            if (linkedStudentFilters.deleted.length) {
                tasks.push({
                    action: () => deleteFormStudentsAssignments({
                        formId: targetFormId,
                        ids: linkedStudentFilters.deleted.map(x => x.assignmentId),
                        linkedStudent: true
                    }),
                    label: 'deleting linked student filters'
                });
            }

            const linkedEmployeeFilters = classifyFilters(privateFormSettings.linkedEmployeeFilters || []);
            const linkedEmployeeGroupFilters = classifyFilters(
                effectiveLinkedToEmployees ? (privateFormSettings.linkedGroupFilters || []) : []
            );
            const createdLinkedEmployeeGroupFilters = linkedEmployeeGroupFilters.created.length
                ? linkedEmployeeGroupFilters.created.map(x => ({
                    filterId: x.id,
                    excluded: x.excluded
                }))
                : [];
            const linkedEmployeePersonAssignments = linkedEmployeeFilters.created.map(x => ({
                personId: x.id,
                name: x.label || `${x.preferredForename ?? ''} ${x.preferredSurname ?? ''}`.trim()
            }));

            if (effectiveLinkedToEmployees === true &&
                (linkedEmployeePersonAssignments.length || createdLinkedEmployeeGroupFilters.length)) {
                tasks.push({
                    action: () => createFormEmployeeAssignment({
                        formId: targetFormId,
                        personAssignments: linkedEmployeePersonAssignments,
                        groupFilters: createdLinkedEmployeeGroupFilters,
                        linkedStudent: true
                    }),
                    label: 'creating linked employee associations'
                });
            }

            if (linkedEmployeeGroupFilters.deleted.length) {
                tasks.push({
                    action: () => deleteFormAssignments({
                        formId: targetFormId,
                        formAssignmentIds: linkedEmployeeGroupFilters.deleted.map(x => x.groupId),
                        linkedStudent: true
                    }),
                    label: 'deleting linked employee group filters'
                });
            }

            if (linkedEmployeeFilters.deleted.length) {
                tasks.push({
                    action: () => deleteFormStudentsAssignments({
                        formId: targetFormId,
                        ids: linkedEmployeeFilters.deleted.map(x => x.assignmentId),
                        linkedStudent: true
                    }),
                    label: 'deleting linked employee filters'
                });
            }
        }

        if (!tasks.length) return;

        const errors = [];

        for (const task of tasks) {
            const result = await task.action();

            if (result.error) {
                const reason = result.error.data?.validationErrors?.map(x => x.errorMessage).join('; ');
                errors.push({errorMessage: `Error while ${task.label}. ${reason}`});
            }
        }

        if (errors.length) {
            const error = new Error('Saving failed');
            error.type = 'SaveFormSettingsError';
            error.details = errors;
            throw error;
        }
        if (formId && !isWizardMode) {
            await refetchFormQuestionAssignment();
        }

        await refreshRecipientSettingsFromServer();
    };

    const handleLinkToStudentsChange = (checked) => {
        setLinkToStudents(checked);

        if (!checked) {
            setAllLinkedEmployeesCanSubmit(false);
        }

        setPrivateFormSettings(prev => ({
            ...prev,
            linkedGroupFilters: !checked
                ? (prev.linkedGroupFilters || []).map(x => ({
                    ...x,
                    isDeleted: true
                }))
                : prev.linkedGroupFilters,
            linkedStudentFilters: !checked
                ? (prev.linkedStudentFilters || []).map(x => ({
                    ...x,
                    isDeleted: true
                }))
                : prev.linkedStudentFilters,
            linkedApplicationFilters: !checked
                ? (prev.linkedApplicationFilters || []).map(x => ({
                    ...x,
                    isDeleted: true
                }))
                : prev.linkedApplicationFilters,
            linkedEnquiryFilters: !checked
                ? (prev.linkedEnquiryFilters || []).map(x => ({
                    ...x,
                    isDeleted: true
                }))
                : prev.linkedEnquiryFilters,
            linkedStrictFiltering: checked ? prev.linkedStrictFiltering : false,
            allLinkedEmployeesCanSubmit: checked ? prev.allLinkedEmployeesCanSubmit : false
        }));

        if (!checked) {
            setHasInitialisedLinkedStudents(true);
        } else {
            setHasInitialisedLinkedStudents(false);
        }

        if (isWizardMode && typeof setWizard === 'function') {
            setWizard(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    linkToStudents: checked,
                    allLinkedEmployeesCanSubmit: checked
                        ? prev.details.allLinkedEmployeesCanSubmit
                        : false,
                    linkedStrictFiltering: checked
                        ? prev.details.linkedStrictFiltering
                        : false,
                }
            }));
        }
    };

    const handleLinkToEmployeesChange = (checked) => {
        setLinkToEmployees(checked);

        if (!checked) {
            setAllLinkedEmployeesCanSubmit(false);
            setHasInitialisedLinkedEmployees(true);
        } else {
            setHasInitialisedLinkedEmployees(false);
        }

        setPrivateFormSettings(prev => ({
            ...prev,
            linkedGroupFilters: !checked
                ? (prev.linkedGroupFilters || []).map(x => ({
                    ...x,
                    isDeleted: true
                }))
                : prev.linkedGroupFilters,
            linkedEmployeeFilters: !checked
                ? (prev.linkedEmployeeFilters || []).map(x => ({
                    ...x,
                    isDeleted: true
                }))
                : prev.linkedEmployeeFilters,
            linkedStrictFiltering: checked ? prev.linkedStrictFiltering : false,
            allLinkedEmployeesCanSubmit: checked ? prev.allLinkedEmployeesCanSubmit : false
        }));

        if (isWizardMode && typeof setWizard === 'function') {
            setWizard(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    linkToEmployees: checked,
                    allLinkedEmployeesCanSubmit: checked
                        ? prev.details.allLinkedEmployeesCanSubmit
                        : false,
                    linkedStrictFiltering: checked
                        ? prev.details.linkedStrictFiltering
                        : false,
                }
            }));
        }
    };

    const handleAllLinkedEmployeesCanSubmitChange = (checked) => {
        setAllLinkedEmployeesCanSubmit(checked);

        setPrivateFormSettings(prev => ({
            ...prev,
            allLinkedEmployeesCanSubmit: checked
        }));

        if (isWizardMode && typeof setWizard === 'function') {
            setWizard(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    allLinkedEmployeesCanSubmit: checked
                }
            }));
        }
    };

    const isFormIncomplete = () => !schema ||
        Object.values(schemaValidation).filter(Boolean).length !== 0 ||
        !accessLevel ||
        !name ||
        remindInterval === '' ||
        !privateFormSettings.validation.isValid;
    const isGoLiveNowUnavailable =
        !showGoLiveActions ||
        isFormSaving ||
        isPublishingForm ||
        isFormIncomplete() ||
        (!hasRecipients && !canMakeLiveWithoutRecipients) ||
        isExpirationInPast;
    const isScheduleGoLiveUnavailable =
        !showGoLiveActions ||
        isFormSaving ||
        isPublishingForm ||
        ((isFormIncomplete() || isExpirationInPast) && !hasExistingScheduledGoLive);
    const goLiveDisabledMessage =
        !hasRecipients && !canMakeLiveWithoutRecipients
            ? 'Add at least one recipient before making this form live'
            : '';

    const getFormValidationMessage = useMemo(() => {
        if (!name) return 'Please enter form name';

        const validationMessages = schemaValidation
            ? Object.values(schemaValidation).filter(Boolean)
            : [];

        if (validationMessages.length) {
            return validationMessages.join('; ');
        }

        if (!privateFormSettings.validation.isValid) {
            return privateFormSettings.validation.error;
        }

        return null;
    }, [
        name,
        schemaValidation,
        privateFormSettings.validation.isValid,
        privateFormSettings.validation.error
    ]);

    const handleCategoriesChange = value => setCategories(value.target.value);

    const handleSetAudience = (value) => {
        if (isWizardMode) return;

        const newAccessLevelsByAudience = {
            [FORM_AUDIENCE_TYPE.enquiries]: FORM_ACCESS_LEVELS.publicWithStudentLink,
            [FORM_AUDIENCE_TYPE.externalUsers]: FORM_ACCESS_LEVELS.publicWithStudentLink,
            [FORM_AUDIENCE_TYPE.previousSchool]: FORM_ACCESS_LEVELS.publicWithStudentLink,
            [FORM_AUDIENCE_TYPE.students]: FORM_ACCESS_LEVELS.private,
            [FORM_AUDIENCE_TYPE.employees]: FORM_ACCESS_LEVELS.private,
            default: accessLevel
        };

        const newAccessLevel = newAccessLevelsByAudience[value] || newAccessLevelsByAudience.default;
        const newAssignmentType = value === FORM_AUDIENCE_TYPE.employees
            ? FORM_ASSIGNMENT_TYPE.employees
            : FORM_ASSIGNMENT_TYPE.students;

        setSelectedAudience(value);
        setAccessLevelRules(newAccessLevel, value);

        if (value === FORM_AUDIENCE_TYPE.enquiries) {
            setLocalParentAudienceType(PARENT_AUDIENCE_TYPE.enquiry);
        } else if (value !== FORM_AUDIENCE_TYPE.parentsForChildCompletion) {
            setLocalParentAudienceType(PARENT_AUDIENCE_TYPE.none);
        }

        setPrivateFormSettings(prev => {
            const next = {
                ...prev,
                assignmentType: newAssignmentType,
            };

            if (prev.triggerFormSettings) {
                next.triggerFormSettings = sanitizeTriggerRecipientFieldIds(
                    value,
                    prev.triggerFormSettings
                );
            }

            return next;
        });

        if (value !== FORM_AUDIENCE_TYPE.employees) {
            setLinkToStudents(false);
            setLinkToEmployees(false);
            setAllLinkedEmployeesCanSubmit(false);
            setPrivateFormSettings(prev => ({
                ...prev,
                linkedGroupFilters: [],
                linkedStudentFilters: [],
                linkedApplicationFilters: [],
                linkedEnquiryFilters: [],
                linkedEmployeeFilters: [],
                linkedStrictFiltering: false
            }));
        }
    };

    const handleGoLiveNow = async () => {
        if (!formId) {
            showErrorNotification('Form must be saved before it can go live');
            return false;
        }

        if (!(await confirmGoLive())) {
            return false;
        }

        try {
            const saved = await saveFormAndAccessSettings();

            if (!saved) {
                return false;
            }

            const isTriggerFormWithEmailField =
                isTriggerForm &&
                !!privateFormSettings.triggerFormSettings?.relatedFormEmailFieldId;

            if (Number(audience) === FORM_AUDIENCE_TYPE.externalUsers && !isTriggerFormWithEmailField) {
                const externalEmails = (privateFormSettings.externalUserEmailAssignments ?? [])
                    .filter((x) => !x.isDeleted && !x.deleted)
                    .map((x) => x.email)
                    .filter(Boolean);

                if (!externalEmails.length) {
                    showErrorNotification('Add at least one external recipient before making this form live');
                    return false;
                }

                const saveExternalResult = await createExternalUserFormAssignments({
                    formId,
                    emails: externalEmails,
                });

                if (saveExternalResult.error) {
                    showErrorNotification('Failed to save external recipients');
                    return false;
                }
            }

            const result = await updateFormStatus({id: formId});

            if (result.error) {
                showErrorNotification(result.error.data);
                return false;
            }

            showInfoNotification(isCommunicationForm ? 'Communication sent' : 'Form is now live');
            navigateToFormsAfterPublish(isCommunicationForm);
            return true;
        } catch {
            showErrorNotification('Failed to publish form');
            return false;
        }
    };

    const handleScheduleGoLive = async (isoDate) => {
        if (!formId) {
            showErrorNotification('Form must be saved before it can be scheduled to go live');
            return false;
        }

        try {
            await saveForm(handleSaveForm({
                targetFormId: formId,
                scheduledActivationAtOverride: isoDate,
            }));

            setPrivateFormSettings(prev => ({
                ...prev,
                scheduledActivationAt: isoDate,
            }));

            if (formAccessRef.current) {
                try {
                    await formAccessRef.current.saveAccessSettings({suppressSuccessNotification: true});
                } catch {

                }
            }

            showSuccessNotification(isoDate == null ? 'Scheduled live date removed' : 'Live date scheduled');
            return true;
        } catch {
            showErrorNotification(isoDate == null ? 'Failed to remove scheduled live date' : 'Failed to schedule live date');
            return false;
        }
    };

    const handleScheduledGoLiveDateChange = (newDate) => {
        setScheduledGoLiveDate(newDate);

        if (!newDate || !expirationMoment) {
            setScheduledGoLiveDateError('');
            return;
        }

        if (newDate.isAfter(expirationMoment)) {
            setScheduledGoLiveDateError('Activation date must be on or before the expiration date.');
            return;
        }

        setScheduledGoLiveDateError('');
    };

    const handleOpenGoLiveDialog = () => {
        setIsGoLiveDialogOpen(true);
    };

    const handleCloseGoLiveDialog = () => {
        setIsGoLiveDialogOpen(false);
    };

    const handleOpenScheduleGoLiveDialog = () => {
        setScheduledGoLiveDate(
            privateFormSettings.scheduledActivationAt
                ? moment(privateFormSettings.scheduledActivationAt)
                : null
        );
        setScheduledGoLiveDateError('');
        setIsScheduleGoLiveDialogOpen(true);
    };

    const handleCloseScheduleGoLiveDialog = () => {
        setScheduledGoLiveDate(
            privateFormSettings.scheduledActivationAt
                ? moment(privateFormSettings.scheduledActivationAt)
                : null
        );
        setScheduledGoLiveDateError('');
        setIsScheduleGoLiveDialogOpen(false);
    };

    const handleConfirmGoLiveNow = async () => {
        const isSuccessful = await handleGoLiveNow();

        if (isSuccessful) {
            setIsGoLiveDialogOpen(false);
        }
    };

    const handleConfirmScheduleGoLive = async () => {
        if ((!scheduledGoLiveDate && !hasExistingScheduledGoLive) || scheduledGoLiveDateError) {
            return;
        }

        const isSuccessful = await handleScheduleGoLive(
            scheduledGoLiveDate ? scheduledGoLiveDate.toDate().toISOString() : null
        );

        if (isSuccessful) {
            setIsScheduleGoLiveDialogOpen(false);
        }
    };

    const handleCommunicationTypeChange = (isEmailCommunication) => {
        setIsCommunicationForm(isEmailCommunication);
    };

    const handleCreateFormFromSetupFlow = async () => {
        if (isFormIncomplete() || isFormSaving || isPublishingForm) {
            return;
        }

        const saved = await saveFormChanges();

        if (!saved) {
            return;
        }

        navigate(`/forms/setup/${formId}`);
    };

    const tabs = [
        {
            label: 'Form Builder',
            component: <FormStructure
                formRef={formRef}
                isWizardMode={isWizardMode}
                accessLevel={accessLevel}
                name={name}
                schema={schema}
                templateId={template?.id}
                formCategories={formCategories}
                onNameChange={handleNameChange}
                onSchemaChange={setSchema}
                onSchemaValidationChange={handleSchemaValidationChange}
                editMode
                expirationDate={expirationDate}
                remindInterval={remindInterval}
                onRemindIntervalChange={handleRemindIntervalChange}
                onExpirationDateChange={setExpirationDate}
                isFormArchived={isReadOnlyForm}
                isCommunication={isCommunicationForm}
                formId={formId}
                categories={categories}
                onCategoriesChange={handleCategoriesChange}
                componentToEditIndex={componentToEditIndex}
                setComponentToEditIndex={setComponentToEditIndex}
                audience={audience}
                parentAudienceType={parentAudienceType}
                recipientType={recipientType}
                linkedToStudents={effectiveLinkedToStudents}
                linkedToEmployees={effectiveLinkedToEmployees}
                triggerSourceAudience={triggerSourceAudience}
                scrollContainerRef={scrollContainerRef}
                onScrollContainerScroll={onScrollContainerScroll}
            />
        },
        {
            label: 'Recipients & Settings',
            component: accessLevel === FORM_ACCESS_LEVELS.private ||
            accessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink
                ? <PrivateFormSettings
                    formId={formId}
                    name={name}
                    expirationDate={expirationDate}
                    onExpirationDateChange={setExpirationDate}
                    remindInterval={remindInterval}
                    categories={categories}
                    onNameChange={setName}
                    onRemindIntervalChange={handleRemindIntervalChange}
                    onCategoriesChange={setCategories}
                    formCategories={formCategories}
                    isFormLive={formStatus === FORM_STATUSES.active}
                    isFormArchived={isReadOnlyForm}
                    strictRecipientsFiltering={strictRecipientsFiltering}
                    audience={audience}
                    parentAudienceType={parentAudienceType}
                    audienceOptions={audienceOptions}
                    allParentsCanSubmit={allParentsCanSubmit}
                    sendToPriority1ContactsOnly={sendToPriority1ContactsOnly}
                    onRollOnly={Boolean(template?.onRollOnly)}
                    visibleToParents={visibleToParents}
                    shareResponseWithOtherParents={shareResponseWithOtherParents}
                    showResponsesInLinkedProfile={showResponsesInLinkedProfile}
                    onShowResponsesInLinkedProfileChange={(checked) => {
                        setShowResponsesInLinkedProfile(checked);
                        if (!checked) setShowResponsesToLinkedParents(false);
                    }}
                    showResponsesToLinkedParents={showResponsesToLinkedParents}
                    onShowResponsesToLinkedParentsChange={setShowResponsesToLinkedParents}
                    allowMultipleSubmissions={allowMultipleSubmissions}
                    enableRecipientNotifications={enableRecipientNotifications}
                    accessLevel={accessLevel}
                    notificationEmails={notificationEmails}
                    onSetStrictRecipientsFiltering={setStrictRecipientsFiltering}
                    onAudienceChange={handleSetAudience}
                    onParentAudienceTypeChange={setLocalParentAudienceType}
                    settings={privateFormSettings}
                    originalRecipientSettings={originalRecipientSettings}
                    externalUsers={externalUserFormAssignments}
                    onSettingsChange={setPrivateFormSettings}
                    profileMustBeComplete={profileMustBeComplete}
                    onAllParentsCanSubmitChange={setAllParentsCanSubmit}
                    onSendToPriority1ContactsOnlyChange={setSendToPriority1ContactsOnly}
                    onVisibleToParentsChange={setVisibleToParents}
                    onShareResponseWithOtherParentsChange={setShareResponseWithOtherParents}
                    onProfileMustBeComplete={setProfileMustBeComplete}
                    onAllowMultipleSubmissionsChange={setAllowMultipleSubmissions}
                    onEnableRecipientNotificationsChange={setEnableRecipientNotifications}
                    onAccessLevelChange={setAccessLevel}
                    onNotificationEmailsChange={setNotificationEmails}
                    isCommunicationForm={isCommunicationForm}
                    onCommunicationTypeChange={handleCommunicationTypeChange}
                    canSwitchToEmail={canSwitchToEmail}
                    formQuestionAssigment={formQuestionAssigment}
                    enableTrigger={enableTrigger}
                    setEnableTrigger={setEnableTrigger}
                    linkToStudents={linkToStudents}
                    onLinkToStudentsChange={handleLinkToStudentsChange}
                    linkToEmployees={linkToEmployees}
                    onLinkToEmployeesChange={handleLinkToEmployeesChange}
                    allLinkedEmployeesCanSubmit={allLinkedEmployeesCanSubmit}
                    onAllLinkedEmployeesCanSubmitChange={handleAllLinkedEmployeesCanSubmitChange}
                    hasInitialisedRecipients={hasInitialisedRecipients}
                    formAccessRef={formAccessRef}
                    schema={schema}
                    enableAutoresponder={enableAutoresponder}
                    onAutoresponderToggle={setEnableAutoresponder}
                    recipientType={recipientType}
                />
                : <PublicFormSettings
                    formId={formId}
                    name={name}
                    onNameChange={setName}
                    formCategories={formCategories}
                    categories={categories}
                    onCategoriesChange={setCategories}
                    notificationEmails={notificationEmails}
                    onNotificationEmailsChange={setNotificationEmails}
                    expirationDate={expirationDate}
                    onExpirationDateChange={setExpirationDate}
                    isFormArchived={isReadOnlyForm}
                    formAccessRef={formAccessRef}
                    schema={schema}
                    enableAutoresponder={enableAutoresponder}
                    onAutoresponderToggle={setEnableAutoresponder}
                    accessLevel={accessLevel}
                    audience={audience}
                    recipientType={recipientType}
                    linkedToStudents={effectiveLinkedToStudents}
                    linkedToEmployees={effectiveLinkedToEmployees}
                    isCommunicationForm={isCommunicationForm}
                    templateType={template?.type}
                    formType={publicSettingsFormType}
                />
        },
    ];

    const getBackdropMessage = () => {
        if (isFormSaving) return 'Saving form';
        if (isPublishingForm) return 'Publishing form';
        return '';
    };

    const StickyFooter = styled('div')(({theme}) => ({
        position: 'sticky',
        bottom: 0,
        left: 0,
        // AD-2659: the bar ends at Felicity's side panel while it is open.
        right: 'var(--felicity-panel-width, 0px)',
        backgroundColor: theme.palette.background.paper,
        borderTop: '1px solid #e0e0e0',
        padding: theme.spacing(2),
        paddingRight: `max(${FELICITY_FAB_CLEARANCE}px - var(--felicity-panel-width, 0px), ${theme.spacing(2)})`,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        flexWrap: 'wrap',
        zIndex: 999,
    }));

    const renderGoLiveActionButtons = () => {
        if (!showGoLiveActions || isSetupFlow) {
            return null;
        }

        return (
            <Stack spacing={1.25}>
                <Tooltip
                    title={goLiveDisabledMessage}
                    placement="top"
                    disableHoverListener={!goLiveDisabledMessage}
                >
                    <span>
                        <Button
                            variant="contained"
                            sx={{
                                width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                                px: {xs: 0, lg: 1},
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: '#155FA7',
                                    boxShadow: 'none',
                                },
                            }}
                            disabled={isGoLiveNowUnavailable}
                            onClick={handleOpenGoLiveDialog}
                        >
                            Go Live
                        </Button>
                    </span>
                </Tooltip>
                <Button
                    variant="outlined"
                    sx={{width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE, px: {xs: 0, lg: 1}}}
                    disabled={isScheduleGoLiveUnavailable}
                    onClick={handleOpenScheduleGoLiveDialog}
                >
                    Schedule Go Live
                </Button>
            </Stack>
        );
    };

    const renderBackToFormsButton = () => {
        return (
            <Button
                variant="outlined"
                sx={{width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE, px: {xs: 0, lg: 1}}}
                onClick={handleExitWithoutSave}
            >
                Back to Forms
            </Button>
        );
    };

    const renderPrimaryActionButton = (isFixedBar) => {
        if (!canSaveForm) {
            return null;
        }

        if (isSetupFlow) {
            return (
                <Box sx={{ml: 'auto'}}>
                    <Tooltip title={getFormValidationMessage}>
                        <span>
                            <Button
                                variant="contained"
                                sx={{
                                    width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                                    px: {xs: 0, lg: 1},
                                    boxShadow: 'none',
                                    mr: isFixedBar ? 4 : 0,
                                    '&:hover': {
                                        backgroundColor: '#155FA7',
                                        boxShadow: 'none',
                                    },
                                }}
                                disabled={isFormIncomplete() || isFormSaving || isPublishingForm}
                                onClick={handleCreateFormFromSetupFlow}
                            >
                                Create form
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            );
        }

        return (
            <Box sx={{ml: 'auto'}}>
                <Tooltip title={getFormValidationMessage}>
                    <span>
                        <Button
                            variant="contained"
                            sx={{
                                width: FOOTER_ACTION_BUTTON_WIDTH_RESPONSIVE,
                                px: {xs: 0, lg: 1},
                                boxShadow: 'none',
                                mr: isFixedBar ? 4 : 0,
                                '&:hover': {
                                    backgroundColor: '#155FA7',
                                    boxShadow: 'none',
                                },
                            }}
                            disabled={isFormIncomplete() || isFormSaving || isPublishingForm}
                            onClick={() =>
                                confirmSave(async () => {
                                    await saveFormAndAccessSettings();
                                })
                            }
                        >
                            Save form
                        </Button>
                    </span>
                </Tooltip>
            </Box>
        );
    };

    const renderRightActionButtons = (isFixedBar = false) => {
        const goLiveActions = renderGoLiveActionButtons();
        const primaryAction = renderPrimaryActionButton(false);

        if (!goLiveActions && !primaryAction) {
            return null;
        }

        return (
            <Box
                sx={(theme) => ({
                    pointerEvents: 'auto',
                    pr: isFixedBar
                        // AD-2659: clear of Felicity's button; while her side panel is open the bar already ends at it.
                        ? `calc(${theme.spacing(4)} + max(${FELICITY_FAB_CLEARANCE}px - var(--felicity-panel-width, 0px), 0px))`
                        : 0,
                })}
            >
                <Stack spacing={1.25} alignItems="flex-end">
                    {goLiveActions}
                    {primaryAction}
                </Stack>
            </Box>
        );
    };

    return (
        <>
            <Box
                sx={{
                    overflowX: 'auto',
                    maxWidth: '100%',
                    '&::-webkit-scrollbar': {height: '4px'},
                    '&::-webkit-scrollbar-track': {backgroundColor: '#f1f1f1'},
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {backgroundColor: '#a1a1a1'},
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#c1c1c1 #f1f1f1',
                }}
            >
                <Paper
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        minHeight: isWizardMode ? 0 : '100%',
                        minWidth: isWizardMode ? {xs: '760px', xl: '1000px'} : '1000px',
                    }}
                    variant={isWizardMode ? undefined : 'outlined'}
                >
                    {!isWizardMode && isEditMode && (
                        <>
                            <AdmicityTabs
                                tabs={isSetupFlow ? [tabs[0]] : tabs}
                                displayDivider
                            />

                            {(isSmallScreen || isAtBottom) && (
                                <StickyFooter>
                                    {renderBackToFormsButton()}
                                    {renderRightActionButtons(false)}
                                </StickyFooter>
                            )}
                        </>
                    )}

                    {!isWizardMode && isEditMode && !isSmallScreen && !isAtBottom && (
                        <Box
                            sx={(theme) => ({
                                position: 'fixed',
                                bottom: theme.spacing(2),
                                left: theme.spacing(2),
                                // AD-2659: the bar ends at Felicity's side panel while it is open.
                                right: 'var(--felicity-panel-width, 0px)',
                                zIndex: theme.zIndex.drawer + 2,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-end',
                                pointerEvents: 'none',
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    pointerEvents: 'auto',
                                    paddingLeft: theme.spacing(2),
                                })}
                            >
                                <Stack spacing={1.25}>
                                    {renderBackToFormsButton()}
                                </Stack>
                            </Box>

                            {renderRightActionButtons(true)}
                        </Box>
                    )}

                    {isWizardMode && tabs[0].component}

                    <AdmicityDialog
                        open={isGoLiveDialogOpen}
                        handleClose={handleCloseGoLiveDialog}
                        title={isCommunicationForm ? 'Send Communication' : 'Go Live'}
                        actions={[
                            {
                                label: 'Cancel',
                                onClick: handleCloseGoLiveDialog,
                            },
                            {
                                label: 'Make Live Now',
                                onClick: handleConfirmGoLiveNow,
                                disabled: isGoLiveNowUnavailable,
                            },
                        ]}
                    >
                        <Stack spacing={2} mt={1}>
                            <Typography variant="body2">
                                {isCommunicationForm
                                    ? 'This will save the form and send the communication to all assigned recipients immediately.'
                                    : 'This will save the form and make it live immediately. Email notifications will be sent to all assigned recipients.'}
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                                {name}
                            </Typography>
                        </Stack>
                    </AdmicityDialog>

                    <AdmicityDialog
                        open={isScheduleGoLiveDialogOpen}
                        handleClose={handleCloseScheduleGoLiveDialog}
                        title="Schedule Go Live"
                        maxWidth="md"
                        actions={[
                            {
                                label: 'Cancel',
                                onClick: handleCloseScheduleGoLiveDialog,
                            },
                            {
                                label:
                                    !scheduledGoLiveDate && hasExistingScheduledGoLive
                                        ? 'Remove Live Date'
                                        : 'Schedule Live Date',
                                onClick: handleConfirmScheduleGoLive,
                                disabled:
                                    (!scheduledGoLiveDate && !hasExistingScheduledGoLive) ||
                                    !!scheduledGoLiveDateError ||
                                    isScheduleGoLiveUnavailable,
                            },
                        ]}
                    >
                        <Stack spacing={2} mt={1}>
                            {expirationMoment && (
                                <Alert severity="info">
                                    This form expires on {formatFormExpirationDate(expirationDate)}. The activation
                                    date must be on or before this.
                                </Alert>
                            )}
                            <Typography variant="body2" color="text.secondary">
                                Choose when this form should go live.
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Box sx={{flexGrow: 1}}>
                                    <DateTimePicker
                                        label="Activation date"
                                        format="MMM D, YYYY, HH:mm"
                                        value={scheduledGoLiveDate}
                                        onChange={handleScheduledGoLiveDateChange}
                                        minDateTime={moment()}
                                        maxDateTime={expirationMoment ?? undefined}
                                        ampm={false}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                helperText: scheduledGoLiveDateError
                                                    ? scheduledGoLiveDateError
                                                    : 'Form activates at the selected time when it becomes live.',
                                                inputProps: {readOnly: true},
                                            },
                                            field: {readOnly: true},
                                        }}
                                    />
                                </Box>

                                {scheduledGoLiveDate && (
                                    <IconButton
                                        aria-label="Clear activation date"
                                        onClick={() => {
                                            setScheduledGoLiveDate(null);
                                            setScheduledGoLiveDateError('');
                                        }}
                                        size="small"
                                        sx={{
                                            transform: 'translateY(-10px)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <CancelIcon fontSize="small"/>
                                    </IconButton>
                                )}
                            </Stack>
                        </Stack>
                    </AdmicityDialog>

                    <Backdrop
                        sx={{
                            color: '#fff',
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                        }}
                        open={isFormSaving || isPublishingForm}
                    >
                        <Stack flexDirection="column" alignItems="center">
                            <CircularProgress size={40}/>
                            <Typography>{getBackdropMessage()}</Typography>
                        </Stack>
                    </Backdrop>
                </Paper>
            </Box>
        </>
    );
};

FormEditor.propTypes = {
    wizardAccessLevel: PropTypes.number,
    mode: PropTypes.string.isRequired,
    wizard: PropTypes.object,
    setWizard: PropTypes.func,
    isWizardMode: PropTypes.bool,
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    forceFormId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    audience: PropTypes.number,
    parentAudienceType: PropTypes.number,
    recipientType: PropTypes.string,
    triggerSourceAudience: PropTypes.number,
    linkedToStudents: PropTypes.bool,
    linkedToEmployees: PropTypes.bool,
    wizardAllLinkedEmployeesCanSubmit: PropTypes.bool,
    wizardLinkedStrictFiltering: PropTypes.bool,
    wizardIsCommunicationForm: PropTypes.bool,
    wizardFormType: PropTypes.string,
    onSchemaValidationChange: PropTypes.func,
};

export default FormEditor;
